
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Client } from "@/types/client";

export const useCreateClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clientData: Omit<Client, "id" | "created_at" | "updated_at" | "creator" | "client_assignments">) => {
      console.log("Creating client with data:", clientData);
      
      
      // فحص التكرار المحسن - منع تكرار الاسم العربي أو الإنجليزي أو رقم الجوال
      const duplicateChecks: string[] = [];
      
      // فحص الاسم العربي
      if (clientData.name_ar?.trim()) {
        duplicateChecks.push(`name_ar.eq.${clientData.name_ar.trim()}`);
      }
      
      // فحص الاسم الإنجليزي
      if (clientData.name_en?.trim()) {
        duplicateChecks.push(`name_en.eq.${clientData.name_en.trim()}`);
      }
      
      // فحص رقم الجوال
      if (clientData.mobile?.trim()) {
        duplicateChecks.push(`mobile.eq.${clientData.mobile.trim()}`);
      }

      if (duplicateChecks.length > 0) {
        const { data: existingClient, error: checkError } = await supabase
          .from('clients')
          .select('id, name_ar, name_en, mobile')
          .or(duplicateChecks.join(','))
          .limit(1)
          .maybeSingle();

        if (checkError) {
          console.error("Error checking for existing client:", checkError);
          throw checkError;
        }

        if (existingClient) {
          // تحديد سبب التكرار
          let duplicateReason = '';
          if (existingClient.name_ar === clientData.name_ar) {
            duplicateReason = 'الاسم العربي';
          } else if (existingClient.name_en === clientData.name_en) {
            duplicateReason = 'الاسم الإنجليزي';
          } else if (existingClient.mobile === clientData.mobile) {
            duplicateReason = 'رقم الجوال';
          }
          
          throw new Error(`يوجد عميل مسجل مسبقاً بنفس ${duplicateReason}`);
        }
      }

      // الحصول على معرف الموظف الحالي
      const { data: currentEmployee } = await supabase
        .from("employees")
        .select("id")
        .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
        .single();

      const insertData = {
        name_ar: clientData.name_ar,
        name_en: clientData.name_en,
        mobile: clientData.mobile,
        additional_info: clientData.additional_info,
        passport_info: clientData.passport_info,
        attachments: clientData.attachments,
        status: clientData.status || 'waiting',
        client_type: clientData.client_type || 'direct',
        company_id: clientData.company_id || null,
        company: !clientData.company_id ? clientData.company : null
      };

      const { data: newClient, error } = await supabase
        .from("clients")
        .insert([insertData])
        .select(`
          *,
          companies:company_id (id, name_ar)
        `)
        .single();
      
      if (error) {
        console.error("Error creating client:", error);
        throw error;
      }
      
      const transformedData = {
        ...newClient,
        company: newClient.companies?.name_ar || newClient.company
      };
      
      console.log("Client created successfully:", transformedData);
      return transformedData as Client;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
};
